import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User as FirebaseUser,
  sendPasswordResetEmail
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { User, AuthContextType } from '../types/auth';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Helper function to get courseAccess from sessionStorage
  const getCourseAccessFromStorage = (userId: string): string => {
    try {
      const stored = sessionStorage.getItem(`courseAccess_${userId}`);
      return stored || '';
    } catch (error) {
      console.error('Error reading courseAccess from sessionStorage:', error);
      return '';
    }
  };

  // Helper function to save courseAccess to sessionStorage
  const saveCourseAccessToStorage = (userId: string, courseAccess: string) => {
    try {
      sessionStorage.setItem(`courseAccess_${userId}`, courseAccess);
    } catch (error) {
      console.error('Error saving courseAccess to sessionStorage:', error);
    }
  };

  // Helper function to clear courseAccess from sessionStorage
  const clearCourseAccessFromStorage = (userId: string) => {
    try {
      sessionStorage.removeItem(`courseAccess_${userId}`);
    } catch (error) {
      console.error('Error clearing courseAccess from sessionStorage:', error);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        // Get additional user data from Firestore
        const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
        const userData = userDoc.data();
        
        // Get courseAccess from sessionStorage or use default
        const courseAccess = getCourseAccessFromStorage(firebaseUser.uid) || userData?.courseAccess || '';
        
        setUser({
          id: firebaseUser.uid,
          email: firebaseUser.email || '',
          name: userData?.name || '',
          phoneNumber: userData?.phoneNumber || '',
          accessToken: await firebaseUser.getIdToken(),
          courseAccess: courseAccess,
          batch: Number(userData?.batch) || 9 // Convert to number and default to current batch (9) if not specified
        });
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const userDoc = await getDoc(doc(db, 'users', userCredential.user.uid));
      const userData = userDoc.data();

      if (!userData) {
        throw new Error('User data not found');
      }

      // Get courseAccess from Firestore or use default
      const courseAccess = userData.courseAccess || '';
      
      // Save courseAccess to sessionStorage
      saveCourseAccessToStorage(userCredential.user.uid, courseAccess);

      setUser({
        id: userCredential.user.uid,
        email: userCredential.user.email || '',
        name: userData.name,
        phoneNumber: userData.whatsapp,
        accessToken: await userCredential.user.getIdToken(),
        courseAccess: courseAccess,
        batch: Number(userData.batch) || 9 // Convert to number and default to current batch (9) if not specified
      });
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      if (user) {
        // Clear courseAccess from sessionStorage
        clearCourseAccessFromStorage(user.id);
      }
      await signOut(auth);
      setUser(null);
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  };

  const resetPassword = async (email: string) => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      console.error('Password reset error:', error);
      throw error;
    }
  };

  const value = {
    user,
    login,
    logout,
    resetPassword,
    isAuthenticated: !!user,
    loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}; 